import { Injectable, OnDestroy } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { BehaviorSubject, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import * as CryptoJS from 'crypto-js';
import { ScreenLockerService } from './screen-locker.service';
import { SettingsService } from './settings/settings.service';
import { BrowserStorageService } from './storage/browser-storage.service';

@Injectable({
    providedIn: 'root'
})
export class ScreenSaverService implements OnDestroy {
    private readonly enabledKey = 'Security.ScreenSaver.Enabled';
    private readonly idleSecondsKey = 'Security.ScreenSaver.IdleSeconds';
    private readonly pinKey = 'Security.ScreenSaver.Pin';
    private readonly legacyPinKey = 'General.ScreenSaver.Pin';
    private readonly lockStateKey = 'navizard.screenSaver.locked';
    private readonly lockReturnKey = 'navizard.screenSaver.returnUrl';
    private idleSubscription?: Subscription;
    private settingsSubscription?: Subscription;
    private countdownSubscription?: Subscription;
    private pinRefreshTimer?: ReturnType<typeof setTimeout>;
    private returnUrl: string | null = null;
    private active$ = new BehaviorSubject<boolean>(false);
    readonly idleCountdown$ = new BehaviorSubject<number | null>(null);
    readonly pinState$ = new BehaviorSubject<boolean>(false);
    private enabled = true;
    private idleSeconds = 180;
    private idleSecondsConfigured = false;
    private pinHash = '';
    private mode: 'unlock' | 'setup' = 'unlock';
    private skipCurrentPin = false;
    private started = false;
    private pinRefreshPending = false;

    constructor(
        private screenLocker: ScreenLockerService,
        private router: Router,
        private settingsService: SettingsService,
        private storage: BrowserStorageService
    ) {
        this.router.events
            .pipe(filter((event) => event instanceof NavigationStart))
            .subscribe((event) => {
                this.handleNavigation(event as NavigationStart);
            });

        this.settingsSubscription = this.settingsService.settingChanged.subscribe((setting) => {
            if (!setting) {
                return;
            }

            if (setting.primaryKey === this.pinKey || setting.primaryKey === this.legacyPinKey) {
                this.setPinHash(this.resolvePinHash(setting));
                this.clearPinRefreshPending();
                return;
            }

            if (setting.primaryKey === this.enabledKey) {
                this.applyEnabled(this.resolveBoolean(setting, true));
                return;
            }

            if (setting.primaryKey === this.idleSecondsKey) {
                this.idleSeconds = this.resolveNumber(setting, this.idleSeconds);
                this.idleSecondsConfigured = true;
                this.refreshIdleWatcher();
            }
        });

        this.applyInitialSettings();
        this.restoreLockState();
        this.countdownSubscription = this.screenLocker.remainingSeconds().subscribe((seconds) => {
            this.updateIdleCountdown(seconds);
        });
    }

    start(idleSeconds: number = 180): void {
        if (this.started) {
            return;
        }

        this.started = true;
        if (!this.idleSecondsConfigured) {
            this.idleSeconds = idleSeconds;
        }
        this.refreshIdleWatcher();
    }

    setEnabled(enabled: boolean): void {
        this.applyEnabled(enabled);
    }

    activate(mode: 'unlock' | 'setup' = 'unlock', options?: { skipCurrentPin?: boolean }): void {
        if (!this.enabled || this.active$.value) {
            return;
        }

        const currentUrl = this.router.url;
        if (this.isBlockedRoute(currentUrl)) {
            return;
        }

        this.refreshPinFromSettings();
        this.returnUrl = currentUrl;
        this.mode = mode;
        this.skipCurrentPin = mode === 'setup' ? !!options?.skipCurrentPin : false;
        this.active$.next(true);
        this.updateIdleCountdown(0);
        this.persistLockState();
    }

    deactivate(options?: { navigate?: boolean }): void {
        this.active$.next(false);
        this.mode = 'unlock';
        this.skipCurrentPin = false;
        this.updateIdleCountdown(this.idleCountdown$.value ?? 0);
        const target = this.returnUrl || this.readStoredReturnUrl() || '/';
        this.returnUrl = null;
        this.clearLockState();
        if (options?.navigate === false) {
            if (this.idleSubscription) {
                this.screenLocker.resetTimer();
            }
            return;
        }

        if (this.router.url.startsWith('/screensaver')) {
            this.router.navigateByUrl(target);
        }

        if (this.idleSubscription) {
            this.screenLocker.resetTimer();
        }
    }

    isActive() {
        return this.active$.asObservable();
    }

    isActiveValue(): boolean {
        return this.active$.value;
    }

    isLocked(): boolean {
        return this.active$.value || this.storage.getSession(this.lockStateKey) === true;
    }

    getMode(): 'unlock' | 'setup' {
        return this.mode;
    }

    shouldSkipCurrentPin(): boolean {
        return this.skipCurrentPin;
    }

    hasPin(): boolean {
        return !!this.pinHash;
    }

    isPinRefreshPending(): boolean {
        return this.pinRefreshPending;
    }

    verifyPin(pin: string): boolean {
        if (!pin || !this.pinHash) {
            return false;
        }

        return this.hashPin(pin) === this.pinHash;
    }

    setPin(pin: string): void {
        if (!pin) {
            return;
        }

        const hashed = this.hashPin(pin);
        this.setPinHash(hashed);

        const setting = this.settingsService.getSetting(this.pinKey);
        const legacySetting = this.settingsService.getSetting(this.legacyPinKey);
        const targetSetting = setting ?? legacySetting;
        if (!targetSetting) {
            return;
        }

        if (targetSetting.userSetting) {
            targetSetting.userSetting.primaryValue = hashed;
            return;
        }

        targetSetting.primaryValue = hashed;
    }

    clearPin(): void {
        this.setPinHash('');

        const setting = this.settingsService.getSetting(this.pinKey);
        const legacySetting = this.settingsService.getSetting(this.legacyPinKey);
        const targetSetting = setting ?? legacySetting;
        if (!targetSetting) {
            return;
        }

        if (targetSetting.userSetting) {
            targetSetting.userSetting.primaryValue = '';
            return;
        }

        targetSetting.primaryValue = '';
    }

    private handleIdle(): void {
        if (!this.enabled || this.active$.value) {
            return;
        }

        const currentUrl = this.router.url;
        if (this.isBlockedRoute(currentUrl)) {
            return;
        }

        this.activate('unlock');
    }

    private isBlockedRoute(url: string): boolean {
        const path = url.split('?')[0].split('#')[0];
        return path.startsWith('/auth')
            || path.startsWith('/landing')
            || path.startsWith('/setup')
            || path.startsWith('/reader-calibration')
            || path.startsWith('/screensaver');
    }

    private handleNavigation(_event: NavigationStart): void {
        if (!this.isLocked() || !this.hasPin()) {
            return;
        }

        if (!this.active$.value) {
            this.active$.next(true);
            this.persistLockState();
            return;
        }
    }

    private persistLockState(): void {
        this.storage.setSession(this.lockStateKey, true);
        if (this.returnUrl) {
            this.storage.setSession(this.lockReturnKey, this.returnUrl);
        }
    }

    private restoreLockState(): void {
        const locked = this.storage.getSession(this.lockStateKey) === true;
        if (!locked) {
            return;
        }

        if (!this.active$.value) {
            this.active$.next(true);
        }

        if (!this.returnUrl) {
            this.returnUrl = this.readStoredReturnUrl();
        }
    }

    private clearLockState(): void {
        this.storage.removeSession(this.lockStateKey);
        this.storage.removeSession(this.lockReturnKey);
    }

    private readStoredReturnUrl(): string | null {
        return this.storage.getSession(this.lockReturnKey);
    }

    ngOnDestroy(): void {
        this.idleSubscription?.unsubscribe();
        this.settingsSubscription?.unsubscribe();
        this.countdownSubscription?.unsubscribe();
        if (this.idleSubscription) {
            this.screenLocker.stopTimer();
        }
    }

    private applyInitialSettings(): void {
        const pinSetting = this.settingsService.getSetting(this.pinKey);
        const legacySetting = this.settingsService.getSetting(this.legacyPinKey);
        if (pinSetting) {
            this.setPinHash(this.resolvePinHash(pinSetting));
        } else if (legacySetting) {
            this.setPinHash(this.resolvePinHash(legacySetting));
        }

        const enabledSetting = this.settingsService.getSetting(this.enabledKey);
        if (enabledSetting) {
            this.enabled = this.resolveBoolean(enabledSetting, this.enabled);
        }

        const idleSetting = this.settingsService.getSetting(this.idleSecondsKey);
        if (idleSetting) {
            this.idleSeconds = this.resolveNumber(idleSetting, this.idleSeconds);
            this.idleSecondsConfigured = true;
        }
    }

    private applyEnabled(enabled: boolean): void {
        const nextEnabled = !!enabled;
        if (this.enabled === nextEnabled) {
            return;
        }

        this.enabled = nextEnabled;
        if (!this.enabled && this.active$.value) {
            this.deactivate();
        }

        this.updateIdleCountdown(this.idleCountdown$.value ?? 0);
        this.refreshIdleWatcher();
    }

    private refreshIdleWatcher(): void {
        if (!this.started) {
            return;
        }

        if (!this.enabled) {
            this.stopIdleWatcher();
            return;
        }

        this.stopIdleWatcher();
        this.idleSubscription = this.screenLocker.startWatching(this.idleSeconds).subscribe(() => {
            this.handleIdle();
        });
    }

    private stopIdleWatcher(): void {
        if (!this.idleSubscription) {
            return;
        }

        this.idleSubscription.unsubscribe();
        this.idleSubscription = undefined;
        this.screenLocker.stopTimer();
    }

    private updateIdleCountdown(seconds: number): void {
        if (!this.enabled || this.active$.value || this.isBlockedRoute(this.router.url)) {
            this.idleCountdown$.next(null);
            return;
        }

        this.idleCountdown$.next(seconds > 0 ? seconds : null);
    }

    private resolvePinHash(setting: any): string {
        const value = setting?.userSetting?.primaryValue ?? setting?.primaryValue ?? '';
        return typeof value === 'string' ? value : `${value ?? ''}`;
    }

    private resolveBoolean(setting: any, fallback: boolean): boolean {
        const raw = setting?.userSetting?.primaryValue ?? setting?.primaryValue;
        if (typeof raw === 'boolean') {
            return raw;
        }
        if (typeof raw === 'number') {
            return raw !== 0;
        }
        if (typeof raw === 'string') {
            const normalized = raw.trim().toLowerCase();
            if (normalized === 'true' || normalized === '1' || normalized === 'yes') {
                return true;
            }
            if (normalized === 'false' || normalized === '0' || normalized === 'no') {
                return false;
            }
        }
        return fallback;
    }

    private resolveNumber(setting: any, fallback: number): number {
        const raw = setting?.userSetting?.primaryValue ?? setting?.primaryValue;
        const parsed = typeof raw === 'number' ? raw : Number(raw);
        if (!Number.isFinite(parsed) || parsed <= 0) {
            return fallback;
        }
        return parsed;
    }

    private hashPin(pin: string): string {
        return CryptoJS.SHA256(pin).toString();
    }

    private setPinHash(hash: string): void {
        this.pinHash = hash ?? '';
        this.pinState$.next(!!this.pinHash);
    }

    private refreshPinFromSettings(): void {
        this.pinRefreshPending = true;
        this.clearPinRefreshTimer();
        this.settingsService.refresh();
        this.pinRefreshTimer = setTimeout(() => {
            this.pinRefreshPending = false;
        }, 2000);
    }

    private clearPinRefreshPending(): void {
        this.pinRefreshPending = false;
        this.clearPinRefreshTimer();
    }

    private clearPinRefreshTimer(): void {
        if (!this.pinRefreshTimer) {
            return;
        }

        clearTimeout(this.pinRefreshTimer);
        this.pinRefreshTimer = undefined;
    }
}
